import { useState, useEffect, useMemo } from "react";
import { SnakeGameProps } from ".";

type SnakeGameHook = {
  snakeBlocks: number[][];
  applePos: {
    x: number;
    y: number;
  };
  infoText: string;
  areaSize: number[];
  blockSize: number;
  showGame: boolean | undefined;
  tinyBlocks: boolean;
  showInfo: boolean;
};

const areaSize = [32, 32];

export default function useSnakeGame(
  gameRef: React.RefObject<HTMLDivElement>,
  props: SnakeGameProps
): SnakeGameHook {
  const [showGame, setShowGame] = useState(props.autoShow);
  const [tinyBlocks, setTinyBlocks] = useState(true);
  const [key, setKey] = useState(0);
  const [blockSize, setBlockSize] = useState(16);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameLoop, setGameLoop] = useState(0);
  const [infoText, setInfoText] = useState("");
  const [showInfo, setShowInfo] = useState(false);

  const [tickVelocity, setTickVelocity] = useState(
    props.startTickVelocity || 82
  );
  const [lastPos, setLastPos] = useState([16, 16]);
  const [snakeBlocks, setSnakeBlocks] = useState<Array<Array<number>>>(
    new Array(0)
  );
  const [count, setCount] = useState(5);
  const [direction, setDirection] = useState(0);

  const [applePos, setApplePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    document.addEventListener("keydown", setKeyCode);
  }, []);

  const setKeyCode = (event: KeyboardEvent) => {
    //Teclas preasignadas
    const keys = [65, 37, 68, 38, 87, 39, 83, 40, 82, 72, 79, 80, 88];
    if (keys.find(k => k === event.keyCode)) setKey(event.keyCode);
  };

  useEffect(() => {
    switch (key) {
      case 72: //H
        if (showGame) {
          setShowInfo(s => !s);
          setKey(0);
        }
        break;
      case 82: //R
        if (!gameStarted) {
          setShowGame(true);
          setGameStarted(true);
          setInfoText("Puntos: 0 | Velocidad: 1");
          setSnakeBlocks(new Array(0));

          setTickVelocity(props.startTickVelocity || 82);
          setDirection(0);
          setCount(5);
          changeApple();

          setTimeout(() => {
            if (gameRef.current) {
              gameRef.current.scrollIntoView({
                behavior: "smooth",
                block: "end",
                inline: "nearest"
              });
            }
          }, 100);

          const finalPos = [areaSize[0] / 2, areaSize[0] / 2];
          setLastPos([...finalPos]);
        }
        setKey(0);
        break;
      case 79: //O
        setBlockSize(x => (x > 8 ? x - 8 : 8));
        if (gameRef.current) {
          gameRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest"
          });
        }
        setKey(0);
        break;
      case 80: //P
        setBlockSize(x => x + 8);
        if (gameRef.current) {
          gameRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest"
          });
        }
        setKey(0);
        break;
      case 88: //X
        setTinyBlocks(x => !x);
        setKey(0);
        break;
    }
  }, [key, gameStarted, gameRef]);

  const gameOver = (text: string) => {
    if (gameLoop) clearTimeout(gameLoop);
    setGameStarted(false);
    setInfoText(text);
  };

  //Bucle del juego (Se llama una vez que empieza el juego)
  useEffect(() => {
    if (gameStarted) {
      const gLoop = window.setTimeout(() => {
        //Se modifica la posicion final, segun la direccion correspondiente
        const finalPos = lastPos;
        switch (direction) {
          case 0: //ARRIBA
            finalPos[1] -= 1;
            break;

          case 1: //ABAJO
            finalPos[1] += 1;
            break;

          case 2: //IZQUIERDA
            finalPos[0] -= 1;
            break;

          case 3: //DERECHA
            finalPos[0] += 1;
            break;
        }
        //Cuando la posicion final se encuentra con otro bloque
        //se termina el juego
        if (snakeBlocks.find(b => b[0] === finalPos[0] && b[1] === finalPos[1]))
          gameOver("Te comiste solo. \n Presiona 'R' para reiniciar");
        //Cuando la posicion final se encuentra con el borde del area
        //se termina el juego
        else if (
          finalPos[0] < 0 ||
          finalPos[1] < 0 ||
          finalPos[0] >= areaSize[0] - 2 ||
          finalPos[1] >= areaSize[1] - 2
        )
          gameOver(
            "Te rompiste la cabeza con la pared. \n Presiona 'R' para reiniciar"
          );
        else {
          setLastPos([...finalPos]);
          setGameLoop(gLoop);
        }
      }, tickVelocity);
    } else if (gameLoop) clearTimeout(gameLoop);
    return () => {
      if (!gameStarted && gameLoop) clearTimeout(gameLoop);
    };
    // eslint-disable-next-line
  }, [gameLoop, gameStarted]);

  //Cada vez que cambia la posicion final, se llama a este hook
  useEffect(() => {
    //Se crea un nuevo bloque en la posicion final
    const blocks = snakeBlocks;
    blocks.push([lastPos[0], lastPos[1], count]);
    //Se comprueba cada bloque
    blocks.forEach((block, index) => {
      //Si la "vida" es menor a 1, se quita del array
      //sino se le saca un punto
      if (block[2] <= 1) blocks.splice(index, 1);
      else block[2] -= 1;
    });
    setSnakeBlocks([...blocks]);

    //Se comprueba que la ultima posicion de la viborita
    //y la posicion de la manzana sea igual
    if (lastPos[0] === applePos.x && lastPos[1] === applePos.y) {
      changeApple();
      setCount(x => x + 1);
      if (tickVelocity > 10 && count % 2 === 0) setTickVelocity(x => x - 1);
      setInfoText(
        "Puntos: " +
          (count - 5) +
          " | Velocidad: " +
          Math.abs(tickVelocity - 81)
      );
    }
    // eslint-disable-next-line
  }, [lastPos]);

  useEffect(() => {
    switch (key) {
      case 87:
      case 38:
        if (direction !== 1) setDirection(0);
        break;

      case 83:
      case 40:
        if (direction !== 0) setDirection(1);
        break;

      case 65:
      case 37:
        if (direction !== 3) setDirection(2);
        break;

      case 68:
      case 39:
        if (direction !== 2) setDirection(3);
        break;
    }
  }, [key, direction]);

  const changeApple = () => {
    //Se elige una posicion aleatoria y se le asigna a la manzana
    const _aPos = {
      x: getRandomInt(0, areaSize[0] - 2),
      y: getRandomInt(0, areaSize[1] - 2)
    };
    setApplePos(apple => ({ ...apple, ..._aPos }));
  };

  return useMemo(
    () => ({
      snakeBlocks,
      applePos,
      infoText,
      areaSize,
      blockSize,
      showGame,
      tinyBlocks,
      showInfo
    }),
    [
      snakeBlocks,
      applePos,
      infoText,
      areaSize,
      blockSize,
      showGame,
      tinyBlocks,
      showInfo
    ]
  );
}

function getRandomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min)) + min;
}
